// src/pages/Appli/AppliAdmin/VisitorCenter/components/VisitorDetailsPanel.jsx

import React from "react";

import {
  FiClock,
  FiExternalLink,
  FiMapPin,
  FiMonitor,
  FiNavigation,
} from "react-icons/fi";

import {
  EmptyState,
  LoadingState,
  StatusBadge,
} from "../ui/VisitorCenterUi";

import {
  formatDateTime,
  formatDevice,
  formatDuration,
  formatLocation,
  formatPageLabel,
  formatTime,
  formatTrafficSource,
} from "../utils/visitorFormatters";

function InfoItem({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-[#F8FBFD] p-4">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
        <Icon />
        {label}
      </div>

      <div className="mt-2 break-words text-sm font-bold text-slate-700">
        {value || "—"}
      </div>
    </div>
  );
}

export default function VisitorDetailsPanel({
  visitor,
  loading = false,
}) {
  if (loading) {
    return (
      <LoadingState label="Chargement du parcours..." />
    );
  }

  if (!visitor) {
    return (
      <EmptyState
        title="Sélectionnez un visiteur"
        description="Cliquez sur un visiteur dans la liste ou sur un marqueur de la carte."
      />
    );
  }

  const pageViews = visitor.pageViews || [];

  return (
    <div>
      <div className="border-b border-slate-100 p-5 lg:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-extrabold text-[#123B57]">
                Visiteur #{visitor.id}
              </h3>

              <StatusBadge status={visitor.status} />
            </div>

            <p className="mt-2 text-sm text-slate-500">
              Première visite :{" "}
              {formatDateTime(visitor.first_seen_at)}
            </p>
          </div>

          <div className="rounded-2xl bg-[#EEF7FC] px-4 py-3 text-center">
            <div className="text-2xl font-extrabold text-[#123B57]">
              {visitor.page_views_count || pageViews.length}
            </div>

            <div className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
              pages vues
            </div>
          </div>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <InfoItem
            icon={FiMapPin}
            label="Localisation"
            value={formatLocation(visitor)}
          />

          <InfoItem
            icon={FiMonitor}
            label="Appareil"
            value={formatDevice(visitor)}
          />

          <InfoItem
            icon={FiNavigation}
            label="Provenance"
            value={formatTrafficSource(visitor)}
          />

          <InfoItem
            icon={FiExternalLink}
            label="Page actuelle"
            value={formatPageLabel(
              visitor.current_page,
            )}
          />
        </div>
      </div>

      <div className="p-5 lg:p-6">
        <div className="flex items-center gap-2">
          <FiClock className="text-[#2EA7E0]" />

          <h4 className="font-extrabold text-[#123B57]">
            Parcours du visiteur
          </h4>
        </div>

        <div className="mt-5 space-y-3">
          {pageViews.length ? (
            pageViews.map((pageView, index) => (
              <div
                key={pageView.id}
                className="relative flex gap-4 rounded-2xl border border-slate-200 bg-white p-4"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#123B57] text-sm font-extrabold text-white">
                  {index + 1}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="font-bold text-slate-800">
                    {pageView.page_title ||
                      formatPageLabel(pageView.page)}
                  </div>

                  <div className="mt-1 truncate text-xs text-slate-400">
                    {pageView.page}
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
                    <span className="rounded-full bg-slate-100 px-2.5 py-1">
                      Arrivée :{" "}
                      {formatTime(pageView.entered_at)}
                    </span>

                    <span className="rounded-full bg-slate-100 px-2.5 py-1">
                      Durée :{" "}
                      {pageView.left_at
                        ? formatDuration(
                            pageView.duration_seconds,
                          )
                        : "en cours"}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <EmptyState
              title="Aucun parcours enregistré"
              description="Les pages consultées apparaîtront ici dès que le tracker sera actif."
            />
          )}
        </div>
      </div>
    </div>
  );
}